import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';

export interface Column<T = any> {
    title: React.ReactNode;
    render: (data: T) => React.ReactNode;
    width?: string;
    flex?: string;
    key: string;
    mobileLabel?: string;
    hideMobile?: boolean;
}

export interface TableProps<T = any> {
    columns: Column<T>[];
    data: T[];
    totalRecords?: number;
    loading?: boolean;
    onPageChange?: (page: number, rows: number) => void;
    initialPage?: number;
    initialRows?: number;
    emptyMessage?: string;
    loadingMessage?: string;
    mobileCardView?: boolean;
    keyExtractor?: (item: T, index: number) => string | number;
}

export const FinanceTable = <T extends any>({
                                                 columns,
                                                 data,
                                                 totalRecords = data.length,
                                                 loading = false,
                                                 onPageChange,
                                                 initialPage = 1,
                                                 initialRows = 10,
                                                 emptyMessage = "No Data Found",
                                                 loadingMessage = "Loading...",
                                                 mobileCardView = true,
                                                 keyExtractor = (_, index) => index,
                                             }: TableProps<T>) => {
    const [first, setFirst] = useState(0);
    const [pageCount, setPageCount] = useState(initialRows);
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
    );
    const [displayData, setDisplayData] = useState<T[]>([]);

    useEffect(() => {
        if (!onPageChange) {
            setDisplayData(data.slice(first, first + pageCount));
        } else {
            setDisplayData(data);
        }
    }, [data, first, pageCount, onPageChange]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const mql = window.matchMedia("(max-width: 768px)");
        const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        setIsMobile(mql.matches);
        mql.addEventListener?.("change", onChange);
        return () => mql.removeEventListener?.("change", onChange);
    }, []);

    const handlePageChange = (e: PaginatorPageChangeEvent) => {
        setFirst(e.first);
        setPageCount(e.rows);
        onPageChange?.(e.page + 1, e.rows);
    };

    // Mobile card view
    if (isMobile && mobileCardView) {
        return (
            <Wrapper>
                <MobileContainer>
                    {(displayData.length === 0 || loading) && (
                        <NoData>{loading ? loadingMessage : emptyMessage}</NoData>
                    )}
                    {!loading && displayData.map((row, i) => (
                        <MobileCard key={keyExtractor(row, i)}>
                            {columns
                                .filter(col => !col.hideMobile)
                                .map((col, j) => (
                                    <MobileRow key={j}>
                                        <MobileLabel>
                                            {col.mobileLabel || col.title}
                                        </MobileLabel>
                                        <MobileValue>{col.render(row)}</MobileValue>
                                    </MobileRow>
                                ))}
                        </MobileCard>
                    ))}
                </MobileContainer>
                {totalRecords > 9 && (
                    <PaginatorBar>
                        <Paginator
                            first={first}
                            rows={pageCount}
                            totalRecords={totalRecords}
                            onPageChange={handlePageChange}
                            pageLinkSize={1}
                            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                        />
                    </PaginatorBar>
                )}
            </Wrapper>
        );
    }

    // Desktop table view
    return (
        <Wrapper>
            <Container>
                <HeaderRow>
                    <HeaderTr>
                        {columns.map((col, i) => (
                            <HeaderCell key={i} width={col.width} flex={col.flex}>
                                {col.title}
                            </HeaderCell>
                        ))}
                    </HeaderTr>
                </HeaderRow>
                <tbody>
                {(displayData.length === 0 || loading) && (
                    <NoDataRow>
                        <NoDataCell colSpan={columns.length}>
                            {loading ? loadingMessage : emptyMessage}
                        </NoDataCell>
                    </NoDataRow>
                )}
                {!loading && displayData.map((row, i) => (
                    <Row key={keyExtractor(row, i)}>
                        {columns.map((col, j) => (
                            <CellItem key={j} width={col.width} flex={col.flex}>
                                {col.render(row)}
                            </CellItem>
                        ))}
                    </Row>
                ))}
                </tbody>
            </Container>
            {totalRecords > 9 && (
                <PaginatorBar>
                    <Paginator
                        first={first}
                        rows={pageCount}
                        totalRecords={totalRecords}
                        onPageChange={handlePageChange}
                        pageLinkSize={isMobile ? 1 : 5}
                        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                    />
                </PaginatorBar>
            )}
        </Wrapper>
    );
};

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 10px;
`;

const Container = styled.table`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const HeaderCell = styled.th<{ width?: string; flex?: string }>`
    color: #00000080;
    font-weight: 600;
    font-size: 14px;
    line-height: 18px;
    width: ${p => p.width};
    flex: ${p => p.flex};
    text-align: start;

    @media (max-width: 768px) {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const HeaderRow = styled.thead`
    display: flex;
    background: #F6F6F6;
    border: none;
    border-radius: 10px;
    height: 40px;
`;

const Row = styled.tr`
    display: flex;
    align-items: center;
    height: 47px;
    border-radius: 10px;
    padding: 14px 12px;

    &:nth-child(even) {
        background-color: #E9E9E9;
    }
`;

const Cell = styled.td<{ width?: string; flex?: string }>`
    display: flex;
    align-items: center;
    width: ${p => p.width};
    flex: ${p => p.flex};
    font-family: SF Pro Display;
    font-weight: 600;
    font-size: 13px;
    line-height: 18px;
`;

const HeaderTr = styled.tr`
    display: flex;
    padding: 0 12px;
    align-items: center;
    width: 100%;
`;

const NoData = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    color: #00000080;
    font-weight: 600;
    font-size: 14px;
`;

const PaginatorBar = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    padding: 10px 0;
    background: #fff;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
`;

const CellItem = styled(Cell)`
    span {
        font-family: SF Pro Display;
        font-weight: 600 !important;
        font-size: 13px;
        line-height: 18px;
    }

    @media (max-width: 768px) {
        span {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            width: 100px;
        }
    }
`;

const NoDataRow = styled.tr``;

const NoDataCell = styled.td`
  width: 100%;
  padding: 16px;
  text-align: center;
  color: #00000080;
  font-weight: 600;
  font-size: 14px;
`;

const MobileContainer = styled.div`
    display: none;
    
    @media (max-width: 768px) {
        display: flex;
        flex-direction: column;
        gap: 16px;
        width: 100%;
    }
`;

const MobileCard = styled.div`
    background: linear-gradient(145deg, #ffffff 0%, #fafafa 100%);
    border-radius: 16px;
    padding: 20px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08), 
                0 1px 4px rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
    gap: 12px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 4px;
        height: 100%;
        background: linear-gradient(180deg, #6366f1 0%, #8b5cf6 100%);
        opacity: 0;
        transition: opacity 0.3s ease;
    }
    
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), 
                    0 2px 8px rgba(0, 0, 0, 0.06);
        
        &::before {
            opacity: 1;
        }
    }
    
    &:active {
        transform: translateY(0);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }
`;

const MobileRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    min-height: 28px;
    padding: 8px 0;
    
    &:not(:last-child) {
        border-bottom: 1px solid rgba(0, 0, 0, 0.04);
    }
    
    &:first-child {
        padding-top: 0;
    }
    
    &:last-child {
        padding-bottom: 0;
        border-bottom: none;
    }
`;

const MobileLabel = styled.div`
    color: #64748b;
    font-weight: 600;
    font-size: 12px;
    line-height: 20px;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    flex-shrink: 0;
    min-width: 110px;
    display: flex;
    align-items: center;
`;

const MobileValue = styled.div`
    color: #0f172a;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    text-align: right;
    word-break: break-word;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: flex-end;
`;